import React, { useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import ReportHeader from "./ReportHeader";

const TABS = [
  { id: "query", label: "Query" },
  { id: "chart", label: "Chart" },
];

/**
 * Shared layout for Insight and Funnel reports.
 * Renders: Top bar (optional) | Header row (date + chart type + tabs) | Main (chart) + Right panel.
 *
 * @param {React.ReactNode} topBar - e.g. <AnalyticsTopBar breadcrumbs={...} onSave={...} onClose={...} />
 * @param {"insight" | "funnel" | "retention" | "flow"} reportType
 * @param {object} headerProps - selectedPreset, onTimeFilterChange, chartType, onChartTypeChange (used when headerContent is not set)
 * @param {React.ReactNode} [headerContent] - optional custom header row (date + chart controls); replaces ReportHeader when set (e.g. Flow + auto-event settings)
 * @param {string} activeTab - 'query' | 'chart'
 * @param {(id: string) => void} onTabChange
 * @param {React.ReactNode} children - chart area (main content)
 * @param {React.ReactNode} rightPanel - Query/Chart panel (e.g. QueryBuilderPanel)
 */
export default function ReportLayout({
  topBar,
  reportType,
  headerProps = {},
  headerContent = null,
  activeTab,
  onTabChange,
  children,
  rightPanel,
}) {
  const {
    selectedPreset,
    onTimeFilterChange,
    chartType,
    onChartTypeChange,
    insightSeriesInterval,
    onInsightSeriesIntervalChange,
    showInsightSeriesInterval,
    funnelMeasuring,
    funnelTableColumns,
    onFunnelTableColumnsChange,
    showFunnelTableSettings,
  } = headerProps;

  // Dotted-white canvas — matches BoardViewPage so editors feel like one surface.
  const dottedBg = {
    backgroundColor: "var(--surface-base)",
    backgroundImage: "radial-gradient(circle, var(--border-default) 1px, transparent 1px)",
    backgroundSize: "20px 20px",
  };
  // On mobile (<md) the right query/chart panel becomes a slide-up sheet
  // triggered by a floating "Edit Query" button. Desktop is unchanged.
  const [isQuerySheetOpen, setIsQuerySheetOpen] = useState(false);

  return (
    <div className="flex min-h-0 flex-1" style={dottedBg}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {topBar}

        <div
          className="flex items-center justify-between border-b bg-surface"
          style={{
            fontFamily: "Inter, sans-serif",
            borderColor: "var(--border-default)",
          }}
        >
          <div
            className="flex items-center gap-2 flex-1"
            style={{
              paddingLeft: "1rem",
              paddingRight: "1rem",
              paddingTop: "0.5rem",
              paddingBottom: "0.5rem",
            }}
          >
            {headerContent ?? (
              <ReportHeader
                reportType={reportType}
                selectedPreset={selectedPreset}
                onTimeFilterChange={onTimeFilterChange}
                chartType={chartType}
                onChartTypeChange={onChartTypeChange}
                insightSeriesInterval={insightSeriesInterval}
                onInsightSeriesIntervalChange={onInsightSeriesIntervalChange}
                showInsightSeriesInterval={showInsightSeriesInterval}
                funnelMeasuring={funnelMeasuring}
                funnelTableColumns={funnelTableColumns}
                onFunnelTableColumnsChange={onFunnelTableColumnsChange}
                showFunnelTableSettings={showFunnelTableSettings}
              />
            )}
          </div>

          <div className="flex items-end" style={{ paddingRight: "1rem" }}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTabChange(tab.id)}
                className="relative text-[13px] font-medium transition-colors"
                style={{
                  color: activeTab === tab.id ? "var(--brand-primary)" : "var(--text-muted)",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  paddingTop: "2px",
                  paddingBottom: "8px",
                  lineHeight: "1.2",
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) e.currentTarget.style.color = "var(--text-primary)";
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) e.currentTarget.style.color = "var(--text-muted)";
                  else e.currentTarget.style.color = "var(--brand-primary)";
                }}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <span
                    className="absolute bottom-0"
                    style={{
                      height: "2px",
                      backgroundColor: "var(--brand-primary)",
                      left: "16px",
                      right: "16px",
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Chart + editor: full height, top-aligned so preview lines up with panel */}
        <div className="flex min-h-0 flex-1 items-stretch relative">
          <main
            className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto"
            style={dottedBg}
          >
            {children}
          </main>
          {/* Desktop (md+): inline right panel. */}
          <div className="hidden md:flex">{rightPanel}</div>

          {/* Mobile: floating button to open the query builder as a sheet. */}
          <button
            type="button"
            onClick={() => setIsQuerySheetOpen(true)}
            className="md:hidden fixed bottom-4 right-4 z-30 inline-flex items-center gap-2 rounded-full bg-[color:var(--text-primary)] px-4 py-2.5 text-sm font-medium text-ink-onBrand shadow-lg"
            style={{ fontFamily: "Inter, sans-serif" }}
            aria-label="Open query builder"
          >
            <SlidersHorizontal size={16} />
            Edit query
          </button>
        </div>

        {/* Mobile: full-screen query builder sheet. */}
        {isQuerySheetOpen && (
          <div
            className="md:hidden fixed inset-0 z-[60] bg-[color:var(--surface-overlay)]"
            onClick={() => setIsQuerySheetOpen(false)}
            aria-hidden
          >
            <div
              className="absolute inset-x-0 bottom-0 top-12 flex flex-col bg-surface rounded-t-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="flex items-center justify-between border-b px-4 py-3"
                style={{ borderColor: "var(--border-default)", fontFamily: "Inter, sans-serif" }}
              >
                <div className="text-[13px] font-semibold text-[color:var(--text-primary)]">Query builder</div>
                <button
                  type="button"
                  onClick={() => setIsQuerySheetOpen(false)}
                  className="rounded-lg p-1.5 text-[color:var(--text-muted)] hover:bg-[color:var(--surface-sunken)]"
                  aria-label="Close query builder"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                {rightPanel}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
